import "./style.scss";

import AutoModeIcon from "@mui/icons-material/AutoMode";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { Collapse, Tooltip } from "@mui/material";
import SheetRecapBtn from "components/PartnerStatementsSettings/StatementFormContainer/StatementConfig/SheetRecap";
import { Spinner } from "components/shared/Spinner";
import StyledButton from "components/shared/StyledButton";
import { useTranslation } from "react-i18next";
import type { IStatementTemplate } from "store/partnerStatementsStore";
import type { TFetchStatus } from "store/types";

import googleStaticInvoice from "../../../../assets/img/png/google_static_invoice.png";
import spreadsheetIcon from "../../../../assets/img/spreadsheet-icon.png";

/**
 * Render custom statement on statement drawer for beneficiaries
 */
interface ICustomStatementBlock {
    /**
     * Condition to show the custom statement
     */
    showCustomStatement: boolean;
    /**
     * on click btn action to show the custom statement => it set a state that load the google sheet fetch
     */
    onShowCustomStatement: () => void;
    /**
     * Beneficiary js/html statement
     */
    statementByBeneficiary: string;
    /**
     * Custom statement data
     */
    beneficiaryCustomStatement: IStatementTemplate | undefined;
    status: TFetchStatus;
}

const CustomStatementBlock = ({
    showCustomStatement,
    statementByBeneficiary,
    onShowCustomStatement,
    beneficiaryCustomStatement,
    status,
}: ICustomStatementBlock) => {
    const [t] = useTranslation();

    const onGoogleSheetNavigate = () => {
        window.open(
            `https://docs.google.com/spreadsheets/d/${beneficiaryCustomStatement?.data.spreadsheetId}/edit#gid=${beneficiaryCustomStatement?.data.sheetId}`,
            "_blank",
        );
    };

    return (
        <div className="beneficiary-uses-custom-statements-wrapper">
            <div className="beneficiary-uses-custom-statements-txt">
                <h2>{t("PartnerStatements.CustomStatement")}</h2>
                <span>{t("PartnerStatements.BeneficiaryHasCustomStatement")}</span>
                <div className="beneficiary-custom-statement-recap">
                    <SheetRecapBtn
                        mode="edit"
                        templateName={beneficiaryCustomStatement?.name}
                        onGoogleSheetNavigate={onGoogleSheetNavigate}
                    />
                </div>
            </div>
            {!showCustomStatement && !statementByBeneficiary && (
                <>
                    <div className="beneficiary-custom-statement-generate-info">
                        <div className="generate-info-title-wrapper">
                            <span>{t("PartnerStatements.GenerateGoogleSheetStatement")}</span>
                            <Tooltip
                                title={t("PartnerStatements.GenerateStatementTooltip")}
                                placement="right-end"
                                arrow
                            >
                                <HelpOutlineIcon sx={{ height: "16px", width: "16px" }} />
                            </Tooltip>
                        </div>
                        <div className="generate-info-list">
                            <li>{t("PartnerStatements.ActionInfo1")}</li>
                            <li>{t("PartnerStatements.ActionInfo2")}</li>
                        </div>
                    </div>
                    <StyledButton
                        buttonType="primary"
                        sx={{ maxWidth: "670px", marginTop: "0.5rem" }}
                        onClick={onShowCustomStatement}
                        fullWidth
                    >
                        <AutoModeIcon sx={{ height: "16px", width: "16px" }} />
                        {t("PartnerStatements.GenerateStatement")}
                    </StyledButton>
                </>
            )}
            <Collapse in={showCustomStatement || !!statementByBeneficiary} timeout="auto" unmountOnExit>
                {status === "pending" && !statementByBeneficiary && (
                    <div className="custom-statement-template-pending">
                        <div className="custom-statement-template-pending-text">
                            <span>{t("PartnerStatements.GeneratingStatement")}</span>
                            <span>{t("PartnerStatements.Waiting")}</span>
                        </div>
                        <Spinner />
                    </div>
                )}
                <Collapse in={status === "succeeded" && !!statementByBeneficiary} timeout="auto">
                    <div className="render-custom-statement-head">
                        <h2 className="custom-statement-generate-title">{t("PartnerStatements.GeneratedStatement")}</h2>
                    </div>
                    <div className="render-custom-statement-template" title={beneficiaryCustomStatement?.name}>
                        <div className="custom-statement-template-head">
                            <img src={spreadsheetIcon} style={{ height: "16px", width: "16px" }} />
                            <span className="template-name">{beneficiaryCustomStatement?.name}</span>
                        </div>
                        <div className="template-preview">
                            <img src={googleStaticInvoice} />
                        </div>
                        <span className="view-statement">{`→ ${t("PartnerStatements.ViewStatement")}`}</span>
                        <div
                            className="inner-HTML-data"
                            dangerouslySetInnerHTML={{
                                __html: statementByBeneficiary,
                            }}
                        />
                    </div>
                </Collapse>
            </Collapse>
        </div>
    );
};

export default CustomStatementBlock;
